import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "antd";
import plPL from "antd/locale/pl_PL";
import { createContext, useContext, lazy, Suspense } from "react";

import { PublicLayout } from "./components/Layout";
import { CookieConsent } from "./components/CookieConsent";
import { TrafficTracker } from "./components/TrafficTracker";
import { DashboardPage } from "./pages/DashboardPage";
import { WorksListPage } from "./pages/WorksListPage";
import { WaitingRoomPage } from "./pages/WaitingRoomPage";
const StatsPage = lazy(() =>
  import("./pages/StatsPage").then((module) => ({ default: module.StatsPage })),
);
const WorkDetailsPage = lazy(() =>
  import("./pages/WorkDetailsPage").then((module) => ({
    default: module.WorkDetailsPage,
  })),
);

// ================================
// THEME DEFINITION - Editorial
// ================================
export interface ColorTheme {
  key: string;
  name: string;
  primary: string;
  secondary: string;
  gradient: string;
  bodyBg: string;
  headerBg: string;
  chartColors: {
    total: string;
    books: string;
    movies: string;
    series: string;
  };
}

// Editorial theme - stały motyw kolorystyczny
export const EDITORIAL_THEME: ColorTheme = {
  key: "editorial",
  name: "Editorial",
  primary: "#245747",
  secondary: "#1b4236",
  gradient: "linear-gradient(135deg, #F7F5EF, #E6EBDF)",
  bodyBg: "#F7F5EF",
  headerBg: "#FFFEFA",
  chartColors: {
    total: "#245747",
    books: "#245747",
    movies: "#4B6B88",
    series: "#A6543C",
  },
};

// Theme context (uproszczony - tylko currentTheme)
interface ThemeContextType {
  currentTheme: ColorTheme;
}

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: EDITORIAL_THEME,
});

export const useColorTheme = () => useContext(ThemeContext);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  const currentTheme = EDITORIAL_THEME;

  return (
    <ThemeContext.Provider value={{ currentTheme }}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          locale={plPL}
          theme={{
            token: {
              colorPrimary: currentTheme.primary,
              colorText: "#252824",
              colorTextSecondary: "#686D64",
              colorBgContainer: "#FFFEFA",
              colorBorderSecondary: "#E2E3D9",
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              borderRadius: 8,
            },
            components: {
              Layout: {
                bodyBg: currentTheme.bodyBg,
                headerBg: "#fff",
              },
              Menu: {
                activeBarBorderWidth: 0,
                itemSelectedColor: currentTheme.primary,
                itemColor: "#595959",
              },
              Card: {
                colorBgContainer: "#FFFEFA",
              },
            },
          }}
        >
          <BrowserRouter
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <TrafficTracker />
            <Suspense
              fallback={
                <div className="page-loading" role="status">
                  Ładowanie strony…
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<PublicLayout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="library" element={<WorksListPage />} />
                  <Route path="stats" element={<StatsPage />} />
                  <Route path="waiting-room" element={<WaitingRoomPage />} />
                  <Route path="works/:slug" element={<WorkDetailsPage />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
          <CookieConsent />
        </ConfigProvider>
      </QueryClientProvider>
    </ThemeContext.Provider>
  );
}

export default App;
